import { useEffect, useState } from "react";
import {
  TimePicker,
  Button,
  Typography,
  Space,
  Row,
  Col,
  Tooltip,
  Card,
} from "antd";
import {
  Plus,
  X,
  Copy,
  Save,
  Trash2,
} from "lucide-react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useDispatch, useSelector } from "react-redux";
import { isEqual } from "lodash";
import TimezoneSettings from "./TimezoneSettings";
import {
  createAvailabilitySlots,
  getAvailabilitySlots,
  getAvailabilityTimezone,
  updateAvailabilitySlots,
} from "../../actionCreator/availabilityActionCreator";
import { useAlert } from "../../context/AlertProvider";


dayjs.extend(utc);
dayjs.extend(timezone);

const { Text } = Typography;
const fallbackTimezone = dayjs.tz.guess();

const daysOfWeek = [
  { label: "S", name: "SUNDAY" },
  { label: "M", name: "MONDAY" },
  { label: "T", name: "TUESDAY" },
  { label: "W", name: "WEDNESDAY" },
  { label: "T", name: "THURSDAY" },
  { label: "F", name: "FRIDAY" },
  { label: "S", name: "SATURDAY" },
];

const dayIndexByName = daysOfWeek.reduce((acc, day, index) => {
  acc[day.name] = index;
  return acc;
}, {});

const referenceWeekDates = {
  SUNDAY: "2024-01-07",
  MONDAY: "2024-01-08",
  TUESDAY: "2024-01-09",
  WEDNESDAY: "2024-01-10",
  THURSDAY: "2024-01-11",
  FRIDAY: "2024-01-12",
  SATURDAY: "2024-01-13",
};

const toDayjs = (time) => dayjs(time, "HH:mm:ss");

const sortSlots = (slots) =>
  [...slots].sort((a, b) =>
    toDayjs(a.startTime).isBefore(toDayjs(b.startTime)) ? -1 : 1
  );

const isOverlappingWith = (slotA, slotB) => {
  const aStart = toDayjs(slotA.startTime);
  const aEnd = toDayjs(slotA.endTime);
  const bStart = toDayjs(slotB.startTime);
  const bEnd = toDayjs(slotB.endTime);
  return aStart.isBefore(bEnd) && aEnd.isAfter(bStart);
};

const createEmptyWeeklyHours = () =>
  daysOfWeek.reduce((acc, day, i) => {
    acc[i] = { dayOfWeek: day.name, recurring: true, timeSlots: [] };
    return acc;
  }, {});

const getLocalDayNameFromDayjs = (value) =>
  daysOfWeek[value.day()].name;

const convertUtcAvailabilityToTimezone = (availability, timezoneName) => {
  const mapped = createEmptyWeeklyHours();

  availability?.forEach((day) => {
    const utcDate = referenceWeekDates[day?.dayOfWeek?.toUpperCase()];
    if (!utcDate) return;

    const rawSlots =
      day?.timeSlots?.length
        ? day.timeSlots
        : day?.startTime && day?.endTime
          ? [{ startTime: day.startTime, endTime: day.endTime }]
          : [];

    rawSlots.forEach((slot) => {
      const startUtc = dayjs.utc(`${utcDate}T${slot.startTime}`);
      const endUtc = dayjs.utc(`${utcDate}T${slot.endTime}`);

      if (!startUtc.isValid() || !endUtc.isValid()) return;

      const localStart = startUtc.tz(timezoneName);
      const localEnd = endUtc.tz(timezoneName);
      const localDayName = getLocalDayNameFromDayjs(localStart);
      const localDayIndex = dayIndexByName[localDayName];

      if (localDayIndex === undefined) return;

      mapped[localDayIndex].timeSlots.push({
        startTime: localStart.format("HH:mm:ss"),
        endTime: localEnd.format("HH:mm:ss"),
      });
    });
  });

  Object.keys(mapped).forEach((key) => {
    mapped[key].timeSlots = sortSlots(mapped[key].timeSlots);
  });

  return mapped;
};

const convertTimezoneAvailabilityToUtc = (weeklyHours, timezoneName) => {
  const utcDays = daysOfWeek.reduce((acc, day) => {
    acc[day.name] = [];
    return acc;
  }, {});

  Object.values(weeklyHours).forEach((day) => {
    const localDate = referenceWeekDates[day.dayOfWeek];

    day.timeSlots.forEach((slot) => {
      const localStart = dayjs.tz(
        `${localDate} ${slot.startTime}`,
        "YYYY-MM-DD HH:mm:ss",
        timezoneName,
      );
      const localEnd = dayjs.tz(
        `${localDate} ${slot.endTime}`,
        "YYYY-MM-DD HH:mm:ss",
        timezoneName,
      );

      if (!localStart.isValid() || !localEnd.isValid()) return;

      const utcStart = localStart.utc();
      const utcEnd = localEnd.utc();
      const utcDayName = daysOfWeek[utcStart.day()].name;

      utcDays[utcDayName].push({
        startTime: utcStart.format("HH:mm:ss"),
        endTime: utcEnd.format("HH:mm:ss"),
      });
    });
  });

  return daysOfWeek.map((day) => ({
    dayOfWeek: day.name,
    timeSlots: sortSlots(utcDays[day.name]),
  }));
};

const Availability = () => {
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const availabilitySlots = useSelector(
    (state) => state.availabilityReducer.availabilitySlots
  );

  const [weeklyHours, setWeeklyHours] = useState({});
  const [initialWeeklyHours, setInitialWeeklyHours] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState(fallbackTimezone);

  useEffect(() => {
    dispatch(getAvailabilitySlots());
  }, [dispatch]);

  useEffect(() => {
    const fetchTimezone = async () => {
      const res = await dispatch(getAvailabilityTimezone());
      setSelectedTimezone(res?.data?.timezone || fallbackTimezone);
    };

    fetchTimezone();
  }, [dispatch]);

  useEffect(() => {
    if (availabilitySlots?.length) {
      const mapped = convertUtcAvailabilityToTimezone(
        availabilitySlots,
        selectedTimezone,
      );
      setWeeklyHours(mapped);
      setInitialWeeklyHours(mapped);
    } else if (availabilitySlots && availabilitySlots.length === 0) {
      const empty = createEmptyWeeklyHours();
      setWeeklyHours(empty);
      setInitialWeeklyHours(empty);
    }
  }, [availabilitySlots, selectedTimezone]);

  useEffect(() => {
    if (!Object.keys(initialWeeklyHours).length) return;
    setIsChanged(!isEqual(weeklyHours, initialWeeklyHours));
  }, [weeklyHours, initialWeeklyHours]);

  const handleSaveAvailability = async () => {
    const formattedDays = convertTimezoneAvailabilityToUtc(
      weeklyHours,
      selectedTimezone,
    );

    const payload = {
      recurring: true,
      weekStartDate: null,
      days: formattedDays,
    };

    setIsSaving(true);
    try {
      if (!availabilitySlots?.length) {
        await dispatch(createAvailabilitySlots(payload));
      } else {
        await dispatch(updateAvailabilitySlots(payload));
      }

      showAlert("success", "Availability saved successfully!");
      setInitialWeeklyHours(weeklyHours);
      dispatch(getAvailabilitySlots());
    } catch (err) {
      showAlert("error", "Failed to save availability.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddSlot = (dayIndex) => {
    setWeeklyHours((prev) => {
      const day = prev[dayIndex];
      const lastSlot = day.timeSlots[day.timeSlots.length - 1];
      let newSlot;

      if (lastSlot) {
        const lastEnd = toDayjs(lastSlot.endTime);
        const newStart = lastEnd.add(1, "hour");
        const newEnd = newStart.add(1, "hour");

        if (newEnd.isAfter(dayjs("23:59", "HH:mm"))) {
          showAlert("warning", "Cannot add a slot that goes past midnight.");
          return prev;
        }

        newSlot = {
          startTime: newStart.format("HH:mm:ss"),
          endTime: newEnd.format("HH:mm:ss"),
        };

        const duplicate = day.timeSlots.some(
          (s) =>
            s.startTime === newSlot.startTime && s.endTime === newSlot.endTime
        );
        if (duplicate) {
          showAlert("warning", "This time slot already exists.");
          return prev;
        }

        const overlap = day.timeSlots.some((s) =>
          isOverlappingWith(s, newSlot)
        );
        if (overlap) {
          showAlert("error", "New slot would overlap an existing slot.");
          return prev;
        }
      } else {
        newSlot = { startTime: "09:00:00", endTime: "10:00:00" };
      }

      const updated = { ...prev };
      updated[dayIndex] = {
        ...day,
        timeSlots: sortSlots([...day.timeSlots, newSlot]),
      };
      return updated;
    });
  };

  const handleRemoveSlot = (dayIndex, slotIndex) => {
    setWeeklyHours((prev) => {
      const updated = prev[dayIndex].timeSlots.filter(
        (_, i) => i !== slotIndex
      );
      return { ...prev, [dayIndex]: { ...prev[dayIndex], timeSlots: updated } };
    });
  };

  const handleClearDay = (dayIndex) => {
    setWeeklyHours((prev) => ({
      ...prev,
      [dayIndex]: { ...prev[dayIndex], timeSlots: [] },
    }));
  };

  const handleTimeChange = (dayIndex, slotIndex, key, time) => {
    if (!time) return;
    const formattedTime = time.format("HH:mm:ss");

    setWeeklyHours((prev) => {
      const day = prev[dayIndex];
      const slots = [...day.timeSlots];
      const current = { ...slots[slotIndex], [key]: formattedTime };

      if (!current.startTime || !current.endTime) {
        showAlert("warning", "Both start and end times must be set.");
        return prev;
      }

      const start = toDayjs(current.startTime);
      const end = toDayjs(current.endTime);

      if (start.isSame(end)) {
        showAlert("error", "Start time and end time cannot be the same.");
        return prev;
      }

      if (end.isBefore(start)) {
        showAlert("error", "End time cannot be earlier than start time.");
        return prev;
      }

      const duplicate = slots.some(
        (s, idx) =>
          idx !== slotIndex &&
          s.startTime === current.startTime &&
          s.endTime === current.endTime
      );
      if (duplicate) {
        showAlert("warning", "This time slot already exists.");
        return prev;
      }

      const overlap = slots.some((s, idx) => {
        if (idx === slotIndex) return false;
        return isOverlappingWith(s, current);
      });
      if (overlap) {
        showAlert("error", "This time range overlaps with an existing slot.");
        return prev;
      }

      slots[slotIndex] = { ...slots[slotIndex], ...current };

      // Behavior 1: When you change a slot's end time, shift next slot's start to match
      const nextIdx = slotIndex + 1;
      if (nextIdx < slots.length) {
        // only shift if next slot would start before new end or if exact behavior desired
        const next = { ...slots[nextIdx] };
        const nextStart = toDayjs(next.startTime);
        if (!nextStart.isAfter(end)) {
          // shift next.start to current.end, but ensure next.end remains after next.start
          const shiftedStart = end;
          const shiftedEnd = toDayjs(next.endTime);
          if (shiftedEnd.isAfter(shiftedStart)) {
            slots[nextIdx] = {
              ...next,
              startTime: shiftedStart.format("HH:mm:ss"),
            };
            // also ensure shifting next does not create overlap with the following slot
            // We'll validate after sorting
          } else {
            showAlert(
              "warning",
              "Cannot shift next slot because it would become invalid."
            );
            // keep original next slot
          }
        }
      }

      // After mutation, sort slots to keep order
      const sanitized = sortSlots(slots);

      // Final overlap sanity check after sorting
      for (let a = 0; a < sanitized.length; a++) {
        for (let b = a + 1; b < sanitized.length; b++) {
          if (isOverlappingWith(sanitized[a], sanitized[b])) {
            showAlert(
              "error",
              "Adjustment caused overlapping slots. Reverting change."
            );
            return prev;
          }
        }
      }

      const updatedDay = { ...day, timeSlots: sanitized };
      return { ...prev, [dayIndex]: updatedDay };
    });
  };

  const handleCopyPrevious = (dayIndex) => {
    if (dayIndex === 0) return;
    const prevDay = weeklyHours[dayIndex - 1];
    if (!prevDay?.timeSlots?.length) {
      showAlert("warning", "No slots to copy from previous day.");
      return;
    }

    // clone and ensure dayOfWeek is correct
    const cloned = prevDay.timeSlots.map((s) => ({ ...s }));
    setWeeklyHours((prev) => ({
      ...prev,
      [dayIndex]: {
        ...prevDay,
        dayOfWeek: daysOfWeek[dayIndex].name,
        timeSlots: sortSlots(cloned),
      },
    }));
  };

  return (
    <Row gutter={[24, 24]} className="availability tw-mt-6">
      <Col xs={24} md={14}>
        <Card
          className="!tw-rounded-2xl !tw-border-slate-100 tw-shadow-[0_10px_30px_rgba(0,0,0,0.02)]"
        >
          <div className="tw-mt-2 tw-mb-6">
            <h3 className="tw-text-base tw-font-bold tw-text-slate-800 tw-mb-1">Weekly Hours</h3>
            <p className="tw-text-xs tw-text-slate-500">
              Set when you’re typically available for meetings.
            </p>
          </div>

          {Object.values(weeklyHours).map((day, i) => (
            <div
              key={i}
              className="tw-flex tw-items-start tw-gap-4 tw-mb-5 last:tw-mb-0"
            >
              <div className="tw-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-indigo-50 tw-text-xs tw-font-bold tw-text-theme tw-border tw-border-solid tw-border-indigo-100/50">
                {daysOfWeek[i].label}
              </div>
              <div className="tw-flex-1">
                <div className="tw-text-sm tw-font-bold tw-text-slate-700 tw-mb-2">
                  {daysOfWeek[i].name}
                </div>

                {day.timeSlots.length > 0 ? (
                  day.timeSlots.map((slot, j) => (
                    <Space key={j} align="center" className="tw-mb-3" wrap>
                      <TimePicker
                        format="h:mm a"
                        value={toDayjs(slot?.startTime)}
                        onChange={(time) =>
                          handleTimeChange(i, j, "startTime", time)
                        }
                        minuteStep={15}
                        className="!tw-rounded-lg !tw-border-slate-200"
                      />
                      <Text className="tw-text-slate-400">-</Text>
                      <TimePicker
                        format="h:mm a"
                        value={toDayjs(slot?.endTime)}
                        onChange={(time) =>
                          handleTimeChange(i, j, "endTime", time)
                        }
                        minuteStep={15}
                        className="!tw-rounded-lg !tw-border-slate-200"
                      />

                      <Tooltip title="Remove slot">
                        <Button
                          type="text"
                          icon={<X size={16} className="tw-text-slate-400 hover:tw-text-red-500" />}
                          onClick={() => handleRemoveSlot(i, j)}
                          className="hover:tw-bg-slate-50 !tw-rounded-lg"
                        />
                      </Tooltip>

                      {j === day.timeSlots.length - 1 && (
                        <>
                          <Tooltip title="Add new slot">
                            <Button
                          type="text"
                              icon={<Plus size={16} className="tw-text-slate-500 hover:tw-text-theme" />}
                              onClick={() => handleAddSlot(i)}
                              className="hover:tw-bg-slate-50 !tw-rounded-lg"
                            />
                          </Tooltip>
                          <Tooltip title="Copy from previous day">
                            <Button
                              type="text"
                              icon={<Copy size={16} className="tw-text-slate-500 hover:tw-text-theme" />}
                              onClick={() => handleCopyPrevious(i)}
                              className="hover:tw-bg-slate-50 !tw-rounded-lg"
                            />
                          </Tooltip>
                          <Tooltip title="Clear full day">
                            <Button
                              type="text"
                              icon={<Trash2 size={16} className="tw-text-slate-500 hover:tw-text-red-500" />}
                              onClick={() => handleClearDay(i)}
                              className="hover:tw-bg-slate-50 !tw-rounded-lg"
                            />
                          </Tooltip>
                        </>
                      )}
                    </Space>
                  ))
                ) : (
                  <Button
                    type="dashed"
                    size="small"
                    icon={<Plus size={14} />}
                    onClick={() => handleAddSlot(i)}
                    className="!tw-rounded-lg tw-text-xs tw-font-semibold tw-border-slate-300 hover:tw-text-theme hover:tw-border-theme"
                  >
                    Add slot
                  </Button>
                )}
              </div>
            </div>
          ))}

          <div className="tw-flex tw-justify-end tw-mt-6">
            <Button
              type="primary"
              className="theme-btn tw-h-[42px] !tw-rounded-xl tw-font-bold tw-text-sm tw-shadow-[0_8px_20px_rgba(93,68,255,0.25)] hover:tw-shadow-[0_12px_24px_rgba(93,68,255,0.35)] hover:tw-scale-[1.01] tw-transition-all tw-duration-300"
              icon={<Save size={16} />}
              loading={isSaving}
              onClick={handleSaveAvailability}
              disabled={!isChanged}
            >
              Save Availability
            </Button>
          </div>
        </Card>
      </Col>

      <Col xs={24} md={10}>
        <TimezoneSettings onTimezoneChange={setSelectedTimezone} />
      </Col>
    </Row>
  );
};

export default Availability;
